import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface Stats {
  totalConversations: number;
  activeConversations: number;
  completedToday: number;
  avgDuration: number;
}

interface RecentConversation {
  id: string;
  channel: 'web' | 'phone';
  status: string;
  contactId: string | null;
  startedAt: string | null;
  createdAt: string;
}

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats>({
    totalConversations: 0,
    activeConversations: 0,
    completedToday: 0,
    avgDuration: 0,
  });
  const [recentConversations, setRecentConversations] = useState<RecentConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [healthStatus, setHealthStatus] = useState<Record<string, { status: string }>>({});

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Load conversations
      const convResponse = await api.get<{
        items: RecentConversation[];
        total: number;
      }>('/conversations?pageSize=5');

      setRecentConversations(convResponse.data?.items || []);

      // Calculate stats
      const total = convResponse.data?.total || 0;
      const active = convResponse.data?.items.filter((c) => c.status === 'active').length || 0;

      setStats({
        totalConversations: total,
        activeConversations: active,
        completedToday: 0, // Would need date filter
        avgDuration: 0, // Would need calculation
      });

      // Load health status
      const healthResponse = await api.get<{
        checks: Record<string, { status: string }>;
      }>('/health/ready');
      setHealthStatus(healthResponse.data?.checks || {});
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-white">Dashboard</h1>
        <p className="mt-1 text-surface-400">Welcome back! Here's an overview of your voice platform.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Conversations"
          value={stats.totalConversations}
          icon={<ChatIcon />}
          color="primary"
        />
        <StatCard
          title="Active Now"
          value={stats.activeConversations}
          icon={<ActiveIcon />}
          color="accent"
        />
        <StatCard
          title="Completed Today"
          value={stats.completedToday}
          icon={<CheckIcon />}
          color="secondary"
        />
        <StatCard
          title="Avg Duration"
          value={`${stats.avgDuration}s`}
          icon={<ClockIcon />}
          color="surface"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Conversations */}
        <div className="lg:col-span-2 card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-white">Recent Conversations</h2>
            <Link to="/conversations" className="text-sm text-primary-400 hover:text-primary-300">
              View all →
            </Link>
          </div>
          <div className="card-body p-0">
            {isLoading ? (
              <div className="p-6 text-center text-surface-500">Loading...</div>
            ) : recentConversations.length === 0 ? (
              <div className="p-6 text-center text-surface-500">
                <p>No conversations yet.</p>
                <Link to="/test-call" className="text-primary-400 hover:text-primary-300 mt-2 inline-block">
                  Start a test call →
                </Link>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-surface-800">
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                      Channel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-800">
                  {recentConversations.map((conv) => (
                    <tr key={conv.id} className="hover:bg-surface-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <Link
                          to={`/conversations/${conv.id}`}
                          className="text-sm font-mono text-primary-400 hover:text-primary-300"
                        >
                          {conv.id.slice(0, 12)}...
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`badge ${
                            conv.channel === 'web' ? 'badge-info' : 'badge-warning'
                          }`}
                        >
                          {conv.channel}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={conv.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-surface-400">
                        {new Date(conv.startedAt || conv.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* System Status */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-display text-lg font-semibold text-white">System Status</h2>
          </div>
          <div className="card-body space-y-4">
            {Object.entries(healthStatus).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-surface-300 capitalize">{key.replace('_', ' ')}</span>
                <StatusBadge status={value.status} />
              </div>
            ))}
            {Object.keys(healthStatus).length === 0 && (
              <p className="text-sm text-surface-500">Loading status...</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="font-display text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/test-call"
            className="card p-6 hover:border-primary-500/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-600/20 text-primary-400 group-hover:bg-primary-600/30 transition-colors">
                <PhoneIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">Start Test Call</h3>
                <p className="text-sm text-surface-400">Test the voice assistant</p>
              </div>
            </div>
          </Link>

          <Link
            to="/conversations"
            className="card p-6 hover:border-secondary-500/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary-600/20 text-secondary-400 group-hover:bg-secondary-600/30 transition-colors">
                <ChatIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">View History</h3>
                <p className="text-sm text-surface-400">Browse past conversations</p>
              </div>
            </div>
          </Link>

          <Link
            to="/settings"
            className="card p-6 hover:border-accent-500/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-600/20 text-accent-400 group-hover:bg-accent-600/30 transition-colors">
                <SettingsIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-medium text-white">Configure</h3>
                <p className="text-sm text-surface-400">Manage integrations</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'accent' | 'surface';
}) {
  const colorClasses = {
    primary: 'bg-primary-600/20 text-primary-400',
    secondary: 'bg-secondary-600/20 text-secondary-400',
    accent: 'bg-accent-600/20 text-accent-400',
    surface: 'bg-surface-700 text-surface-400',
  };

  return (
    <div className="card p-6">
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-surface-400">{title}</p>
          <p className="text-2xl font-display font-bold text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusClasses: Record<string, string> = {
    healthy: 'badge-success',
    configured: 'badge-success',
    active: 'badge-success',
    completed: 'badge-info',
    pending: 'badge-warning',
    connecting: 'badge-warning',
    not_configured: 'badge-warning',
    failed: 'badge-error',
    unhealthy: 'badge-error',
    cancelled: 'badge-error',
  };

  return <span className={`badge ${statusClasses[status] || 'badge-info'}`}>{status}</span>;
}

// Icons
function ChatIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function ActiveIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function CheckIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ClockIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
    </svg>
  );
}

function PhoneIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function SettingsIcon({ className = 'h-6 w-6' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}




